/**
 * Seat layouts, and why a ticket needs no cryptography.
 *
 * LAYOUT
 * ------
 * `seats_business = 8, seats_regular = 40` cannot draw a seat map: a picker
 * needs rows, an aisle, and labels. Rather than ship a drawing tool, a
 * transporter picks a template and a row count and we generate the map, then
 * they can override individual seats (block the one by the door, promote a row
 * to business). Generated labels are the ones passengers already expect —
 * row number plus a letter, aisle skipped, "1A 1B | 1C 1D".
 *
 * TICKETS
 * -------
 * The driver scans on a highway with no signal, so verification has to work
 * offline. The obvious answer is a signed QR, but signing means shipping an
 * HMAC implementation into a React Native app and a secret alongside it.
 *
 * None of that is necessary. The driver app downloads the trip manifest when
 * the driver STARTS the trip — which happens at the depot, on wifi — and the
 * manifest carries every live ticket's token. Verification offline is then
 * simply: is this token in the manifest? A token is 24 random bytes, so it
 * cannot be guessed, and a forged QR fails membership. Online sales are frozen
 * once the trip starts, so the manifest cannot go stale underneath the driver.
 *
 * No secret on the device, no clock skew, no signature format to version.
 */
const SPLIT = {
    '2+2': [2, 2],
    '2+1': [2, 1],
    '3+2': [3, 2],
    '2+0': [2, 0],
    '1+1': [1, 1],
};
export const SEAT_TEMPLATES = Object.keys(SPLIT).map((t) => ({
    value: t,
    label: `${t}  (${SPLIT[t][0] + SPLIT[t][1]} per row)`,
    perRow: SPLIT[t][0] + SPLIT[t][1],
}));
const LETTERS = 'ABCDEFGH';
/**
 * Build a layout from a template. Pure, so the seat picker in the browser and
 * the manifest on the server always agree on what seat 3C is.
 */
export function generateSeatLayout(input) {
    const { template, rows } = input;
    const businessRows = Math.max(0, Math.min(input.businessRows ?? 0, rows));
    const blocked = new Set(input.blocked ?? []);
    const split = SPLIT[template];
    const perRow = split[0] + split[1];
    const seats = [];
    for (let row = 1; row <= rows; row++) {
        for (let col = 1; col <= perRow; col++) {
            const label = `${row}${LETTERS[col - 1]}`;
            seats.push({
                label,
                class: row <= businessRows ? 'business' : 'regular',
                row_no: row,
                col_no: col,
                blocked: blocked.has(label),
            });
        }
    }
    return { template, rows, split, seats };
}
/** Counts that must match vehicles.seats_business / seats_regular. */
export function countSeats(layout) {
    let business = 0;
    let regular = 0;
    for (const s of layout.seats) {
        if (s.blocked)
            continue;
        if (s.class === 'business')
            business++;
        else
            regular++;
    }
    return { business, regular };
}
/**
 * Where the aisle falls, for rendering. Returns the column index AFTER which
 * the gap is drawn, or null when the template has no aisle (2+0).
 */
export function aisleAfterColumn(layout) {
    return layout.split[1] === 0 ? null : layout.split[0];
}
/** Seats grouped into rows, ready to render. */
export function seatRows(layout) {
    const byRow = new Map();
    for (const s of layout.seats) {
        const list = byRow.get(s.row_no) ?? [];
        list.push(s);
        byRow.set(s.row_no, list);
    }
    return [...byRow.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([, list]) => list.sort((a, b) => a.col_no - b.col_no));
}
// ---------------------------------------------------------------- tickets
/** What the QR encodes. Prefixed so a scanner can reject foreign codes fast. */
export const TICKET_QR_PREFIX = 'TB1:';
/**
 * A whole booking in one code, for a charter.
 *
 * Its own prefix rather than a flag inside the payload: the scanner has to
 * know which kind of thing it is holding BEFORE it looks anything up, and a
 * prefix is checkable without a database.
 */
export const GROUP_QR_PREFIX = 'TBG1:';
export function ticketQrPayload(ticketToken) {
    return `${TICKET_QR_PREFIX}${ticketToken}`;
}
export function groupQrPayload(groupToken) {
    return `${GROUP_QR_PREFIX}${groupToken}`;
}
const TOKEN_SHAPE = /^[A-Za-z0-9_-]{16,64}$/;
/** Pull the token back out of a scanned code; null if it is not one of ours. */
export function parseTicketQr(scanned) {
    const raw = scanned.trim();
    if (!raw.startsWith(TICKET_QR_PREFIX))
        return null;
    const token = raw.slice(TICKET_QR_PREFIX.length);
    return TOKEN_SHAPE.test(token) ? token : null;
}
/**
 * Either kind, told apart. Group is tested FIRST: 'TB1:' is not a prefix of
 * 'TBG1:', but keeping the order explicit stops that being a subtle
 * dependency on the two strings never changing.
 */
export function parseAnyTicketQr(scanned) {
    const raw = scanned.trim();
    if (raw.startsWith(GROUP_QR_PREFIX)) {
        const token = raw.slice(GROUP_QR_PREFIX.length);
        return TOKEN_SHAPE.test(token) ? { kind: 'group', token } : null;
    }
    const seat = parseTicketQr(raw);
    return seat ? { kind: 'seat', token: seat } : null;
}
// ---------------------------------------------------------------- money
/**
 * Tax is applied at sale time and then frozen on the booking. The rate is the
 * country's, because that is what decides it — the transporter enters a fare
 * and the jurisdiction adds its own VAT/GST/PST on top.
 */
export function priceSeats(fare, seatCount, taxRate) {
    const round2 = (n) => Math.round(n * 100) / 100;
    const fare_amount = round2(fare * seatCount);
    const tax_amount = round2(fare_amount * taxRate);
    return { fare_amount, tax_amount, total_amount: round2(fare_amount + tax_amount) };
}
//# sourceMappingURL=seats.js.map